// src/pages/ScanMagnetMessage.tsx
import React, { FormEvent, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardBody, CenteredPage, Form, FormLabel, Heading, Image, Text, TextInput } from '../components/ui';
import { restrictedAccessService } from '../services/RestrictedAccessService';

const ScanMagnetMessage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ message: string; isError: boolean } | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const settings = await restrictedAccessService.getPublicSettings();
        if (!cancelled) {
          setEnabled(settings.enabled);
          setStartedAt(Date.now());
        }
      } catch (error) {
        if (!cancelled) {
          setEnabled(false);
        }
      } finally {
        if (!cancelled) {
          setLoadingSettings(false);
        }
      }
    };

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      const refreshedStartedAt = Date.now();
      const response = await restrictedAccessService.requestLoginLink(email, {
        website,
        startedAt,
      });
      setStatus({
        isError: false,
        message: response.message,
      });
      setEmail('');
      setWebsite('');
      setStartedAt(refreshedStartedAt);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'Impossible d envoyer le lien de connexion.';
      setStatus({
        isError: true,
        message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CenteredPage light padding={4} style={{ width: '100vw' }}>
      <Image
        src={`${process.env.PUBLIC_URL}/marmite_original.png`} // Assuming you have a magnet image or a relevant app icon
        alt="La Marmite Familiale"
        style={{ width: '150px', height: '150px', marginBottom: '20px' }}
      />
      <Heading as="h2" marginBottom={3} textColor="primary">Accès Restreint</Heading>
      <Text variant="lead">
        Scanne le magnet pour ouvrir la marmite familiale !
      </Text>
      <Text as="small" tone="muted" marginTop={3}>
        Utilise ton téléphone pour lire le tag NFC ou le QR code.
      </Text>

      {!loadingSettings && enabled && (
        <Card fullWidth marginTop={4} shadow="sm" style={{ maxWidth: '420px' }}>
          <CardBody textAlign="start">
            <Heading as="h3" marginBottom={3} textAlign="center" visualLevel="h5">Recevoir mon lien de connexion</Heading>
            <Text tone="muted" marginBottom={3} variant="small">
              Saisis ton e-mail pour recevoir un lien de connexion personnel.
            </Text>

            {status && (
              <Alert variant={status.isError ? 'danger' : 'success'} marginBottom={3}>
                {status.message}
              </Alert>
            )}

            <Form onSubmit={(event) => { void handleSubmit(event); }}>
              <Box
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                <FormLabel htmlFor="restricted-access-website">Site web</FormLabel>
                <TextInput
                  id="restricted-access-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </Box>
              <FormLabel htmlFor="restricted-access-email">
                Adresse e-mail
              </FormLabel>
              <TextInput
                id="restricted-access-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                inputMode="email"
                maxLength={254}
                required
              />
              <Button type="submit" fullWidth marginTop={3} disabled={submitting}>
                {submitting ? 'Envoi en cours...' : 'Recevoir mon lien de connexion'}
              </Button>
            </Form>
          </CardBody>
        </Card>
      )}
    </CenteredPage>
  );
};

export default ScanMagnetMessage;
